import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Tabs,
  Tab,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Tooltip,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  LinearProgress,
  Autocomplete,
} from '@mui/material';
import {
  Assessment,
  Download,
  Refresh,
  Schedule,
  TrendingUp,
  TrendingDown,
  AttachMoney,
  Receipt,
  AccountBalance,
  Business,
  DateRange,
  PictureAsPdf,
  TableChart,
  InsertChart,
  Email,
  Delete,
  Visibility,
  CalendarToday,
  FilterList,
  SaveAlt,
  PlayArrow,
} from '@mui/icons-material';
import { Switch, FormControlLabel } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format, subDays, startOfMonth, endOfMonth, startOfYear, endOfYear } from 'date-fns';
import { reportService, partnerService } from '../services/api';
import { parseDate } from '../utils/dateUtils';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => {
  return (
    <div role="tabpanel" hidden={value !== index}>
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
};

interface ReportConfig {
  id: string;
  name: string;
  description: string;
  category: 'financial' | 'operational' | 'compliance' | 'analytics';
  icon: React.ReactElement;
  parameters: ReportParameter[];
}

interface ReportParameter {
  name: string;
  label: string;
  type: 'date' | 'daterange' | 'select' | 'multiselect' | 'text' | 'partner';
  required: boolean;
  options?: { value: string; label: string }[];
}

interface ReportHistory {
  id: string;
  reportType: string;
  reportName: string;
  generatedAt: string;
  generatedBy: string;
  status: 'COMPLETED' | 'PROCESSING' | 'FAILED';
  fileUrl?: string;
  fileName?: string;
  fileSize?: number;
  parameters: any;
}

const Reports: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const [selectedReport, setSelectedReport] = useState<ReportConfig | null>(null);
  const [generateDialogOpen, setGenerateDialogOpen] = useState(false);
  const [reportHistory, setReportHistory] = useState<ReportHistory[]>([]);
  const [loading, setLoading] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [notification, setNotification] = useState<{
    type: 'success' | 'error' | 'info';
    message: string;
  } | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [reportToDelete, setReportToDelete] = useState<ReportHistory | null>(null);
  const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false);
  const [scheduleParams, setScheduleParams] = useState({
    frequency: 'DAILY',
    time: '08:00',
    enabled: true,
    emailRecipients: '',
  });

  // Report parameters state
  const [reportParams, setReportParams] = useState<any>({
    dateFrom: subDays(new Date(), 30),
    dateTo: new Date(),
    format: 'PDF',
    partner: null,
    status: 'ALL',
    currency: 'ALL',
    reportType: 'SUMMARY',
  });

  // Partner search for reports
  const [partners, setPartners] = useState<any[]>([]);
  const [partnerSearchLoading, setPartnerSearchLoading] = useState(false);

  const reportConfigs: ReportConfig[] = [
    // Financial Reports
    {
      id: 'transaction-summary',
      name: 'Transaction Summary Report',
      description: 'Comprehensive summary of all transactions with totals and breakdowns',
      category: 'financial',
      icon: <Receipt />,
      parameters: [
        { name: 'dateFrom', label: 'Start Date', type: 'date', required: true },
        { name: 'dateTo', label: 'End Date', type: 'date', required: true },
        {
          name: 'currency',
          label: 'Currency',
          type: 'select',
          required: false,
          options: [
            { value: 'ALL', label: 'All Currencies' },
            { value: 'USD', label: 'USD' },
            { value: 'ZWG', label: 'ZWG' },
          ]
        },
        { name: 'partner', label: 'Partner (Optional)', type: 'partner', required: false },
      ],
    },
    {
      id: 'revenue-report',
      name: 'Revenue & Commission Report',
      description: 'Detailed breakdown of revenue, commissions, and net earnings',
      category: 'financial',
      icon: <AttachMoney />,
      parameters: [
        { name: 'dateFrom', label: 'Start Date', type: 'date', required: true },
        { name: 'dateTo', label: 'End Date', type: 'date', required: true },
        { name: 'partner', label: 'Partner (Optional)', type: 'partner', required: false },
      ],
    },
    {
      id: 'settlement-report',
      name: 'Settlement Report',
      description: 'Partner settlement details including pending and completed settlements',
      category: 'financial',
      icon: <AccountBalance />,
      parameters: [
        { name: 'dateFrom', label: 'Start Date', type: 'date', required: true },
        { name: 'dateTo', label: 'End Date', type: 'date', required: true },
        {
          name: 'status',
          label: 'Settlement Status',
          type: 'select',
          required: false,
          options: [
            { value: 'ALL', label: 'All Statuses' },
            { value: 'PENDING', label: 'Pending' },
            { value: 'COMPLETED', label: 'Completed' },
            { value: 'FAILED', label: 'Failed' },
          ]
        },
        { name: 'partner', label: 'Partner', type: 'partner', required: false },
      ],
    },
    {
      id: 'reconciliation-report',
      name: 'Reconciliation Report',
      description: 'Daily reconciliation with discrepancies and adjustments',
      category: 'financial',
      icon: <TableChart />,
      parameters: [
        { name: 'dateFrom', label: 'Reconciliation Date', type: 'date', required: true },
      ],
    },

    // Operational Reports
    {
      id: 'voucher-lifecycle',
      name: 'Voucher Lifecycle Report',
      description: 'Track vouchers from creation to settlement',
      category: 'operational',
      icon: <Receipt />,
      parameters: [
        { name: 'dateFrom', label: 'Start Date', type: 'date', required: true },
        { name: 'dateTo', label: 'End Date', type: 'date', required: true },
        {
          name: 'status',
          label: 'Voucher Status',
          type: 'select',
          required: false,
          options: [
            { value: 'ALL', label: 'All Statuses' },
            { value: 'ACTIVE', label: 'Active' },
            { value: 'PAID', label: 'Paid' },
            { value: 'CANCELLED', label: 'Cancelled' },
            { value: 'EXPIRED', label: 'Expired' },
          ]
        },
      ],
    },
    {
      id: 'partner-performance',
      name: 'Partner Performance Report',
      description: 'Partner transaction volumes, success rates, and trends',
      category: 'operational',
      icon: <Business />,
      parameters: [
        { name: 'dateFrom', label: 'Start Date', type: 'date', required: true },
        { name: 'dateTo', label: 'End Date', type: 'date', required: true },
        { name: 'partner', label: 'Partner (Optional)', type: 'partner', required: false },
      ],
    },
    {
      id: 'float-utilization',
      name: 'Float Utilization Report',
      description: 'Agent float balances and utilization patterns',
      category: 'operational',
      icon: <AttachMoney />,
      parameters: [
        { name: 'dateFrom', label: 'Report Date', type: 'date', required: true },
      ],
    },

    // Compliance Reports
    {
      id: 'audit-trail',
      name: 'Audit Trail Report',
      description: 'Comprehensive audit log for compliance purposes',
      category: 'compliance',
      icon: <Assessment />,
      parameters: [
        { name: 'dateFrom', label: 'Start Date', type: 'date', required: true },
        { name: 'dateTo', label: 'End Date', type: 'date', required: true },
        {
          name: 'entityType',
          label: 'Entity Type',
          type: 'select',
          required: false,
          options: [
            { value: 'ALL', label: 'All Entities' },
            { value: 'VOUCHER', label: 'Vouchers' },
            { value: 'PARTNER', label: 'Partners' },
            { value: 'TRANSACTION', label: 'Transactions' },
            { value: 'SETTLEMENT', label: 'Settlements' },
          ]
        },
      ],
    },
    {
      id: 'exception-report',
      name: 'Exception & Error Report',
      description: 'Failed transactions and system exceptions',
      category: 'compliance',
      icon: <TrendingDown />,
      parameters: [
        { name: 'dateFrom', label: 'Start Date', type: 'date', required: true },
        { name: 'dateTo', label: 'End Date', type: 'date', required: true },
      ],
    },
    {
      id: 'regulatory-report',
      name: 'Regulatory Compliance Report',
      description: 'Regulatory compliance metrics and KYC status',
      category: 'compliance',
      icon: <Assessment />,
      parameters: [
        { name: 'dateFrom', label: 'Start Date', type: 'date', required: true },
        { name: 'dateTo', label: 'End Date', type: 'date', required: true },
      ],
    },

    // Analytics Reports
    {
      id: 'trend-analysis',
      name: 'Trend Analysis Report',
      description: 'Transaction trends, seasonal patterns, and forecasts',
      category: 'analytics',
      icon: <TrendingUp />,
      parameters: [
        { name: 'dateFrom', label: 'Start Date', type: 'date', required: true },
        { name: 'dateTo', label: 'End Date', type: 'date', required: true },
      ],
    },
    {
      id: 'kpi-dashboard',
      name: 'KPI Dashboard Report',
      description: 'Key performance indicators and business metrics',
      category: 'analytics',
      icon: <InsertChart />,
      parameters: [
        { name: 'dateFrom', label: 'Start Date', type: 'date', required: true },
        { name: 'dateTo', label: 'End Date', type: 'date', required: true },
      ],
    },
  ];

  useEffect(() => {
    fetchReportHistory();
  }, []);

  const fetchReportHistory = async () => {
    setHistoryLoading(true);
    try {
      const response = await reportService.getReportHistory();
      const history = (response.content || response.data || response || []).map((report: any) => ({
        id: report.id,
        reportType: report.reportType || report.type,
        reportName: report.reportName || report.name,
        generatedAt: report.generatedAt || report.createdAt,
        generatedBy: report.generatedBy || report.user,
        status: report.status || 'COMPLETED',
        fileUrl: report.fileUrl || report.downloadUrl,
        fileName: report.fileName,
        fileSize: report.fileSize,
        parameters: report.parameters || {},
      }));
      setReportHistory(history);
    } catch (error) {
      console.error('Failed to fetch report history:', error);
      setReportHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  const searchPartners = async (searchTerm: string) => {
    if (!searchTerm || searchTerm.length < 2) {
      setPartners([]);
      return;
    }

    setPartnerSearchLoading(true);
    try {
      const response = await partnerService.getPartners({ search: searchTerm });
      setPartners(response.content || response.data || response || []);
    } catch (error) {
      console.error('Failed to search partners:', error);
      setPartners([]);
    } finally {
      setPartnerSearchLoading(false);
    }
  };

  const handleGenerateReport = (report: ReportConfig) => {
    setSelectedReport(report);
    setReportParams({
      dateFrom: subDays(new Date(), 30),
      dateTo: new Date(),
      format: 'PDF',
      partner: null,
      status: 'ALL',
      currency: 'ALL',
    });
    setGenerateDialogOpen(true);
  };

  const handleSubmitReport = async () => {
    if (!selectedReport) return;

    setLoading(true);
    try {
      const params: any = {
        format: reportParams.format,
      };

      // Add configured parameters
      selectedReport.parameters.forEach(param => {
        if (param.type === 'date') {
          if (reportParams[param.name]) {
            params[param.name] = format(reportParams[param.name], 'yyyy-MM-dd');
          }
        } else if (param.type === 'partner') {
          if (reportParams.partner) {
            params.partnerId = reportParams.partner.id;
            params.partnerName = reportParams.partner.partnerName;
          }
        } else {
          if (reportParams[param.name] && reportParams[param.name] !== 'ALL') {
            params[param.name] = reportParams[param.name];
          }
        }
      });

      const response = await reportService.generateReport(selectedReport.id, params);

      setNotification({
        type: 'success',
        message: response.message || 'Report generation started. You will be notified when it is ready.',
      });

      setGenerateDialogOpen(false);

      // Refresh history after 2 seconds
      setTimeout(() => {
        fetchReportHistory();
      }, 2000);
    } catch (error: any) {
      console.error('Failed to generate report:', error);
      setNotification({
        type: 'error',
        message: error.response?.data?.message || error.message || 'Failed to generate report. Please try again.',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadReport = async (report: ReportHistory) => {
    try {
      const blob = await reportService.downloadReport(report.id);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = report.fileName || `report-${report.id}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      console.error('Failed to download report:', error);
      setNotification({
        type: 'error',
        message: error.response?.data?.message || 'Failed to download report. Please try again.',
      });
    }
  };

  const handleDeleteReport = async () => {
    if (!reportToDelete) return;

    try {
      await reportService.deleteReport(reportToDelete.id);
      setNotification({
        type: 'success',
        message: 'Report deleted successfully.',
      });
      setDeleteDialogOpen(false);
      setReportToDelete(null);
      fetchReportHistory();
    } catch (error: any) {
      console.error('Failed to delete report:', error);
      setNotification({
        type: 'error',
        message: error.response?.data?.message || 'Failed to delete report. Please try again.',
      });
    }
  };

  const openDeleteDialog = (report: ReportHistory) => {
    setReportToDelete(report);
    setDeleteDialogOpen(true);
  };

  const handleScheduleReport = async () => {
    if (!selectedReport) return;

    try {
      const params: any = {
        reportType: selectedReport.id,
        frequency: scheduleParams.frequency,
        time: scheduleParams.time,
        enabled: scheduleParams.enabled,
      };

      if (scheduleParams.emailRecipients) {
        params.emailRecipients = scheduleParams.emailRecipients.split(',').map(e => e.trim());
      }

      // Add report parameters
      selectedReport.parameters.forEach(param => {
        if (param.type === 'date') {
          if (reportParams[param.name]) {
            params[param.name] = format(reportParams[param.name], 'yyyy-MM-dd');
          }
        } else if (param.type === 'partner') {
          if (reportParams.partner) {
            params.partnerId = reportParams.partner.id;
          }
        } else {
          if (reportParams[param.name] && reportParams[param.name] !== 'ALL') {
            params[param.name] = reportParams[param.name];
          }
        }
      });

      await reportService.scheduleReport(params);

      setNotification({
        type: 'success',
        message: `${selectedReport.name} scheduled successfully.`,
      });

      setScheduleDialogOpen(false);
    } catch (error: any) {
      console.error('Failed to schedule report:', error);
      setNotification({
        type: 'error',
        message: error.response?.data?.message || 'Failed to schedule report. Please try again.',
      });
    }
  };

  const openScheduleDialog = (report: ReportConfig) => {
    setSelectedReport(report);
    setReportParams({
      dateFrom: subDays(new Date(), 30),
      dateTo: new Date(),
      format: 'PDF',
      partner: null,
      status: 'ALL',
      currency: 'ALL',
    });
    setScheduleDialogOpen(true);
  };

  const handleQuickDateRange = (range: string) => {
    const today = new Date();
    let from: Date;
    let to: Date = today;

    switch (range) {
      case 'today':
        from = today;
        break;
      case 'yesterday':
        from = subDays(today, 1);
        to = subDays(today, 1);
        break;
      case 'last7days':
        from = subDays(today, 7);
        break;
      case 'last30days':
        from = subDays(today, 30);
        break;
      case 'thisMonth':
        from = startOfMonth(today);
        to = endOfMonth(today);
        break;
      case 'thisYear':
        from = startOfYear(today);
        to = endOfYear(today);
        break;
      default:
        from = subDays(today, 30);
    }

    setReportParams({ ...reportParams, dateFrom: from, dateTo: to });
  };

  const getCategoryReports = (category: string) => {
    return reportConfigs.filter(r => r.category === category);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'COMPLETED': return 'success';
      case 'PROCESSING': return 'info';
      case 'FAILED': return 'error';
      default: return 'default';
    }
  };

  const formatFileSize = (bytes?: number) => {
    if (!bytes) return 'N/A';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  };

  const filteredReports = reportConfigs.filter(report => {
    if (tabValue === 0) return true;
    if (tabValue === 1) return report.category === 'financial';
    if (tabValue === 2) return report.category === 'operational';
    if (tabValue === 3) return report.category === 'compliance';
    if (tabValue === 4) return report.category === 'analytics';
    return true;
  });

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box>
        {/* Header */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h4">Reports & Analytics</Typography>
          <Box display="flex" gap={2}>
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={fetchReportHistory}
            >
              Refresh
            </Button>
          </Box>
        </Box>

        {/* Notifications */}
        {notification && (
          <Alert
            severity={notification.type}
            sx={{ mb: 3 }}
            onClose={() => setNotification(null)}
          >
            {notification.message}
          </Alert>
        )}

        {/* Info Alert */}
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2">
            Generate comprehensive reports for financial analysis, operational monitoring, compliance audits, and business analytics.
            All reports can be exported in PDF, Excel, or CSV format.
          </Typography>
        </Alert>

        {/* Tabs */}
        <Paper sx={{ mb: 3 }}>
          <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)} variant="fullWidth">
            <Tab label="All Reports" />
            <Tab label="Financial" icon={<AttachMoney />} iconPosition="start" />
            <Tab label="Operational" icon={<Assessment />} iconPosition="start" />
            <Tab label="Compliance" icon={<Receipt />} iconPosition="start" />
            <Tab label="Analytics" icon={<TrendingUp />} iconPosition="start" />
          </Tabs>
        </Paper>

        {/* Report Cards */}
        <Grid container spacing={3} mb={4}>
          {filteredReports.map((report) => (
            <Grid item xs={12} md={6} lg={4} key={report.id}>
              <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <CardHeader
                  avatar={report.icon}
                  title={report.name}
                  subheader={
                    <Chip
                      label={report.category.toUpperCase()}
                      size="small"
                      sx={{ mt: 0.5 }}
                    />
                  }
                />
                <CardContent sx={{ flexGrow: 1 }}>
                  <Typography variant="body2" color="textSecondary">
                    {report.description}
                  </Typography>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="caption" color="textSecondary">
                    Parameters: {report.parameters.map(p => p.label).join(', ')}
                  </Typography>
                </CardContent>
                <Box sx={{ p: 2, pt: 0, display: 'flex', gap: 1 }}>
                  <Button
                    fullWidth
                    variant="contained"
                    startIcon={<PlayArrow />}
                    onClick={() => handleGenerateReport(report)}
                  >
                    Generate
                  </Button>
                  <Tooltip title="Schedule Report">
                    <IconButton
                      color="primary"
                      onClick={() => openScheduleDialog(report)}
                    >
                      <Schedule />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Report History */}
        <Paper sx={{ mb: 3 }}>
          <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Report History</Typography>
            <Button
              startIcon={<Refresh />}
              onClick={fetchReportHistory}
              disabled={historyLoading}
            >
              Refresh
            </Button>
          </Box>
          <Divider />
          {historyLoading ? (
            <Box p={3}>
              <LinearProgress />
            </Box>
          ) : reportHistory.length === 0 ? (
            <Box p={3} textAlign="center">
              <Typography color="textSecondary">
                No reports generated yet. Generate your first report above.
              </Typography>
            </Box>
          ) : (
            <>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Report Name</TableCell>
                      <TableCell>Generated</TableCell>
                      <TableCell>Generated By</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>File Size</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {reportHistory
                      .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                      .map((report) => (
                        <TableRow key={report.id}>
                          <TableCell>
                            <Box display="flex" alignItems="center" gap={1}>
                              <PictureAsPdf color="error" fontSize="small" />
                              <Box>
                                <Typography variant="body2">{report.reportName}</Typography>
                                <Typography variant="caption" color="textSecondary">
                                  {report.reportType}
                                </Typography>
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {format(parseDate(report.generatedAt), 'MMM dd, yyyy HH:mm')}
                            </Typography>
                          </TableCell>
                          <TableCell>{report.generatedBy}</TableCell>
                          <TableCell>
                            <Chip
                              label={report.status}
                              color={getStatusColor(report.status) as any}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>{formatFileSize(report.fileSize)}</TableCell>
                          <TableCell align="right">
                            <Tooltip title="Download Report">
                              <IconButton
                                size="small"
                                onClick={() => handleDownloadReport(report)}
                                disabled={report.status !== 'COMPLETED'}
                              >
                                <Download />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete Report">
                              <IconButton
                                size="small"
                                onClick={() => openDeleteDialog(report)}
                                color="error"
                              >
                                <Delete />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                rowsPerPageOptions={[10, 25, 50]}
                component="div"
                count={reportHistory.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={(e, newPage) => setPage(newPage)}
                onRowsPerPageChange={(e) => {
                  setRowsPerPage(parseInt(e.target.value, 10));
                  setPage(0);
                }}
              />
            </>
          )}
        </Paper>

        {/* Generate Report Dialog */}
        <Dialog
          open={generateDialogOpen}
          onClose={() => setGenerateDialogOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            {selectedReport?.name}
          </DialogTitle>
          <DialogContent>
            {selectedReport && (
              <>
                <Alert severity="info" sx={{ mb: 3 }}>
                  {selectedReport.description}
                </Alert>

                <Grid container spacing={3}>
                  {/* Quick Date Range Buttons */}
                  {selectedReport.parameters.some(p => p.type === 'date') && (
                    <Grid item xs={12}>
                      <Typography variant="caption" color="textSecondary" gutterBottom>
                        Quick Date Ranges:
                      </Typography>
                      <Box display="flex" gap={1} flexWrap="wrap" mt={1}>
                        <Button size="small" onClick={() => handleQuickDateRange('today')}>Today</Button>
                        <Button size="small" onClick={() => handleQuickDateRange('yesterday')}>Yesterday</Button>
                        <Button size="small" onClick={() => handleQuickDateRange('last7days')}>Last 7 Days</Button>
                        <Button size="small" onClick={() => handleQuickDateRange('last30days')}>Last 30 Days</Button>
                        <Button size="small" onClick={() => handleQuickDateRange('thisMonth')}>This Month</Button>
                        <Button size="small" onClick={() => handleQuickDateRange('thisYear')}>This Year</Button>
                      </Box>
                    </Grid>
                  )}

                  {/* Dynamic Parameters */}
                  {selectedReport.parameters.map((param) => (
                    <Grid item xs={12} sm={6} key={param.name}>
                      {param.type === 'date' && (
                        <DatePicker
                          label={param.label}
                          value={reportParams[param.name] || null}
                          onChange={(date) => setReportParams({ ...reportParams, [param.name]: date })}
                          slotProps={{ textField: { fullWidth: true, required: param.required } }}
                        />
                      )}
                      {param.type === 'select' && (
                        <FormControl fullWidth required={param.required}>
                          <InputLabel>{param.label}</InputLabel>
                          <Select
                            value={reportParams[param.name] || 'ALL'}
                            label={param.label}
                            onChange={(e) => setReportParams({ ...reportParams, [param.name]: e.target.value })}
                          >
                            {param.options?.map(opt => (
                              <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      )}
                      {param.type === 'partner' && (
                        <Autocomplete
                          fullWidth
                          options={partners}
                          value={reportParams.partner}
                          onChange={(e, newValue) => setReportParams({ ...reportParams, partner: newValue })}
                          onInputChange={(e, value) => searchPartners(value)}
                          getOptionLabel={(option) => `${option.partnerName} (${option.partnerCode})`}
                          loading={partnerSearchLoading}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label={param.label}
                              required={param.required}
                              InputProps={{
                                ...params.InputProps,
                                endAdornment: (
                                  <>
                                    {partnerSearchLoading ? <CircularProgress size={20} /> : null}
                                    {params.InputProps.endAdornment}
                                  </>
                                ),
                              }}
                            />
                          )}
                        />
                      )}
                    </Grid>
                  ))}

                  {/* Output Format */}
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <InputLabel>Output Format</InputLabel>
                      <Select
                        value={reportParams.format}
                        label="Output Format"
                        onChange={(e) => setReportParams({ ...reportParams, format: e.target.value })}
                      >
                        <MenuItem value="PDF">
                          <Box display="flex" alignItems="center" gap={1}>
                            <PictureAsPdf fontSize="small" /> PDF
                          </Box>
                        </MenuItem>
                        <MenuItem value="EXCEL">
                          <Box display="flex" alignItems="center" gap={1}>
                            <TableChart fontSize="small" /> Excel
                          </Box>
                        </MenuItem>
                        <MenuItem value="CSV">
                          <Box display="flex" alignItems="center" gap={1}>
                            <SaveAlt fontSize="small" /> CSV
                          </Box>
                        </MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setGenerateDialogOpen(false)}>Cancel</Button>
            <Button
              variant="contained"
              onClick={handleSubmitReport}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} /> : <PlayArrow />}
            >
              {loading ? 'Generating...' : 'Generate Report'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Delete Report</DialogTitle>
          <DialogContent>
            <Alert severity="warning" sx={{ mb: 2 }}>
              Are you sure you want to delete this report? This action cannot be undone.
            </Alert>
            {reportToDelete && (
              <Box>
                <Typography variant="body2">
                  <strong>Report:</strong> {reportToDelete.reportName}
                </Typography>
                <Typography variant="body2">
                  <strong>Generated:</strong> {format(parseDate(reportToDelete.generatedAt), 'MMM dd, yyyy HH:mm')}
                </Typography>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
            <Button
              variant="contained"
              color="error"
              onClick={handleDeleteReport}
              startIcon={<Delete />}
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>

        {/* Schedule Report Dialog */}
        <Dialog
          open={scheduleDialogOpen}
          onClose={() => setScheduleDialogOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            Schedule Report: {selectedReport?.name}
          </DialogTitle>
          <DialogContent>
            {selectedReport && (
              <>
                <Alert severity="info" sx={{ mb: 3 }}>
                  Schedule this report to run automatically at specified intervals. Reports will be generated and optionally emailed to recipients.
                </Alert>

                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <InputLabel>Frequency</InputLabel>
                      <Select
                        value={scheduleParams.frequency}
                        label="Frequency"
                        onChange={(e) => setScheduleParams({ ...scheduleParams, frequency: e.target.value })}
                      >
                        <MenuItem value="DAILY">Daily</MenuItem>
                        <MenuItem value="WEEKLY">Weekly</MenuItem>
                        <MenuItem value="MONTHLY">Monthly</MenuItem>
                        <MenuItem value="QUARTERLY">Quarterly</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Time"
                      type="time"
                      value={scheduleParams.time}
                      onChange={(e) => setScheduleParams({ ...scheduleParams, time: e.target.value })}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Email Recipients (Optional)"
                      value={scheduleParams.emailRecipients}
                      onChange={(e) => setScheduleParams({ ...scheduleParams, emailRecipients: e.target.value })}
                      placeholder="email1@example.com, email2@example.com"
                      helperText="Comma-separated email addresses to receive the report"
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={scheduleParams.enabled}
                          onChange={(e) => setScheduleParams({ ...scheduleParams, enabled: e.target.checked })}
                        />
                      }
                      label="Enable Schedule"
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Divider />
                  </Grid>

                  {/* Report Parameters */}
                  {selectedReport.parameters.map((param) => (
                    <Grid item xs={12} sm={6} key={param.name}>
                      {param.type === 'date' && (
                        <DatePicker
                          label={param.label}
                          value={reportParams[param.name] || null}
                          onChange={(date) => setReportParams({ ...reportParams, [param.name]: date })}
                          slotProps={{ textField: { fullWidth: true, required: param.required } }}
                        />
                      )}
                      {param.type === 'select' && (
                        <FormControl fullWidth required={param.required}>
                          <InputLabel>{param.label}</InputLabel>
                          <Select
                            value={reportParams[param.name] || 'ALL'}
                            label={param.label}
                            onChange={(e) => setReportParams({ ...reportParams, [param.name]: e.target.value })}
                          >
                            {param.options?.map(opt => (
                              <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      )}
                      {param.type === 'partner' && (
                        <Autocomplete
                          fullWidth
                          options={partners}
                          value={reportParams.partner}
                          onChange={(e, newValue) => setReportParams({ ...reportParams, partner: newValue })}
                          onInputChange={(e, value) => searchPartners(value)}
                          getOptionLabel={(option) => `${option.partnerName} (${option.partnerCode})`}
                          loading={partnerSearchLoading}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label={param.label}
                              required={param.required}
                              InputProps={{
                                ...params.InputProps,
                                endAdornment: (
                                  <>
                                    {partnerSearchLoading ? <CircularProgress size={20} /> : null}
                                    {params.InputProps.endAdornment}
                                  </>
                                ),
                              }}
                            />
                          )}
                        />
                      )}
                    </Grid>
                  ))}

                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <InputLabel>Output Format</InputLabel>
                      <Select
                        value={reportParams.format}
                        label="Output Format"
                        onChange={(e) => setReportParams({ ...reportParams, format: e.target.value })}
                      >
                        <MenuItem value="PDF">
                          <Box display="flex" alignItems="center" gap={1}>
                            <PictureAsPdf fontSize="small" /> PDF
                          </Box>
                        </MenuItem>
                        <MenuItem value="EXCEL">
                          <Box display="flex" alignItems="center" gap={1}>
                            <TableChart fontSize="small" /> Excel
                          </Box>
                        </MenuItem>
                        <MenuItem value="CSV">
                          <Box display="flex" alignItems="center" gap={1}>
                            <SaveAlt fontSize="small" /> CSV
                          </Box>
                        </MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setScheduleDialogOpen(false)}>Cancel</Button>
            <Button
              variant="contained"
              onClick={handleScheduleReport}
              startIcon={<Schedule />}
            >
              Schedule Report
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </LocalizationProvider>
  );
};

export default Reports;
